'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';
import { useGuidedTour } from '@/lib/hooks/useGuidedTour';
import { HomePage, type HomeData } from '@/components/home/home-page';
import { OperatorHome, type OperatorHomeData } from '@/components/home/operator-home';
import { FetchError } from '@/components/state/error-states';
import { loadHomeData, loadOperatorHomeData } from '@/lib/home-data';

export default function Home() {
  const { user } = useAuth();
  const router = useRouter();
  const role = user?.role ?? 'USER';
  const isOperator = role === 'OPERATOR';
  const hasTenantContext = Boolean(user?.tenantId);
  const { startTour } = useGuidedTour();

  const [home, setHome] = useState<HomeData | null>(null);
  const [platform, setPlatform] = useState<OperatorHomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // The tour speaks in terms of issuing a first certificate, so it only makes
  // sense once the account is actually inside a tenant.
  useEffect(() => {
    if (user && hasTenantContext && role !== 'USER' && !user.hasCompletedOnboarding) {
      const t = setTimeout(() => startTour(), 600);
      return () => clearTimeout(t);
    }
  }, [user, hasTenantContext, role, startTour]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        if (isOperator && !hasTenantContext) {
          const data = await loadOperatorHomeData();
          if (!cancelled) setPlatform(data);
        } else {
          const data = await loadHomeData(user.tenantName ?? 'Your workspace');
          if (!cancelled) setHome(data);
        }
      } catch (err) {
        console.error('[Home] Failed to load', err);
        if (!cancelled) setError('We could not load your dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, isOperator, hasTenantContext, reloadKey]);

  // Signed-out visitors keep the public shell; the dashboard is authenticated-only.
  if (!user) {
    return (
      <div className="mx-auto max-w-[520px] py-24 text-center">
        <h1 className="text-[30px] font-semibold tracking-[-0.015em]">Certificate Trust Portal</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Issue, verify and manage blockchain-backed professional certificates.
        </p>
        <Link
          href="/login"
          className="mt-6 inline-flex h-10 items-center rounded-[var(--radius)] bg-primary px-5 text-sm font-semibold text-primary-foreground"
        >
          Sign in
        </Link>
      </div>
    );
  }

  if (error) {
    return <FetchError onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  if (isOperator && !hasTenantContext) {
    return (
      <OperatorHome
        data={platform ?? { metrics: [], attention: [], tenants: [], newestTenants: [], newestCertificates: [], totals: { tenants: 0, certificates: 0, chains: 0 } }}
        isLoading={loading || !platform}
      />
    );
  }

  return (
    <HomePage
      role={role}
      data={
        home ?? {
          tenant: {
            name: user?.tenantName ?? 'Your workspace',
            initials: '—',
            planName: '—',
            subscriptionStatus: 'active',
            renewalDate: '—',
          },
          cost: { estimatedLabel: '', breakdownLabel: '' },
          meters: {
            certificates: { label: 'Certificates', used: 0, included: 0, max: 0, overageUnitLabel: 'per certificate', unitNoun: 'certificates' },
            seats: { label: 'Seats', used: 0, included: 0, max: 0, overageUnitLabel: 'per seat / month', unitNoun: 'seats' },
          },
          recent: [],
          totalCertificates: 0,
          attention: [],
        }
      }
      isLoading={loading || !home}
      onIssue={() => router.push('/certificates?tab=create')}
    />
  );
}
